/**
 * Entities data table with toolbar, filtering, and detail sheet.
 */

import { useState, useMemo } from "react";
import { Button } from "@/ui/primitives/button";

import { ScrollArea } from "@/ui/primitives/scroll-area";
import { Input } from "@/ui/primitives/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/ui/composed/data-display/table";
import { Sheet, SheetContent } from "@/ui/composed/overlays/sheet";
import {
  HelpCircle,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Search,
} from "lucide-react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
  type SortingState,
  type ColumnFiltersState,
} from "@tanstack/react-table";
import type { EntitiesDataTableProps } from "../types";
import { TYPE_ICON } from "../constants";
import { useEntityData } from "./useEntityData";
import { entityColumns } from "./columns";
import { EntityDetailSheet } from "./EntityDetailSheet";

export function EntitiesDataTable({
  output,
  onDocumentClick,
}: EntitiesDataTableProps) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null);

  const { rows, relationshipIndex, entityTypes } = useEntityData(output);

  // Apply type filter as a column filter
  const effectiveColumnFilters = useMemo(() => {
    const filters = [...columnFilters];
    if (typeFilter) {
      filters.push({ id: "type", value: typeFilter });
    }
    return filters;
  }, [columnFilters, typeFilter]);

  const table = useReactTable({
    data: rows,
    columns: entityColumns,
    state: {
      sorting,
      columnFilters: effectiveColumnFilters,
      globalFilter,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  const totalEntities = rows.length;
  const filteredCount = table.getFilteredRowModel().rows.length;

  const selectedEntity =
    selectedRowIndex !== null ? rows[selectedRowIndex] : null;
  const selectedRelationships =
    selectedRowIndex !== null
      ? relationshipIndex.get(selectedRowIndex)
      : undefined;

  return (
    <div className="flex h-full flex-col gap-3">
      {/* Card: toolbar header pinned above the scrollable table */}
      <div className="flex h-[calc(100vh-440px)] flex-col overflow-hidden rounded-xl border border-border bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)]">
        {/* Toolbar: search + type filter pills */}
        <div className="flex shrink-0 flex-wrap items-center gap-3 border-b border-border p-3">
          <div className="relative min-w-[200px] max-w-sm flex-1">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search entities..."
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              className="h-8 pl-8 text-sm"
            />
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <Button
              variant={typeFilter === null ? "default" : "ghost"}
              size="sm"
              className="h-7 px-2.5 text-xs"
              onClick={() => setTypeFilter(null)}
            >
              All
            </Button>
            {entityTypes.map(([type, count]) => {
              const Icon = TYPE_ICON[type] ?? HelpCircle;
              return (
                <Button
                  key={type}
                  variant={typeFilter === type ? "default" : "ghost"}
                  size="sm"
                  className="h-7 gap-1 px-2.5 text-xs capitalize"
                  onClick={() =>
                    setTypeFilter(typeFilter === type ? null : type)
                  }
                >
                  <Icon className="h-3 w-3" />
                  {type}
                  <span className="ml-0.5 text-muted-foreground">{count}</span>
                </Button>
              );
            })}
          </div>
          {(globalFilter || typeFilter) && (
            <span className="text-xs text-muted-foreground">
              {filteredCount} of {totalEntities}
            </span>
          )}
        </div>

        {/* Data table */}
        <ScrollArea className="min-h-0 flex-1">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id} className="hover:bg-transparent">
                  {headerGroup.headers.map((header) => (
                    <TableHead
                      key={header.id}
                      className="h-9 whitespace-nowrap text-xs"
                    >
                      {header.isPlaceholder ? null : header.column.getCanSort() ? (
                        <button
                          type="button"
                          className="flex items-center gap-1 transition-colors hover:text-foreground"
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                          {header.column.getIsSorted() === "asc" ? (
                            <ArrowUp className="h-3 w-3" />
                          ) : header.column.getIsSorted() === "desc" ? (
                            <ArrowDown className="h-3 w-3" />
                          ) : (
                            <ArrowUpDown className="h-3 w-3 text-muted-foreground/50" />
                          )}
                        </button>
                      ) : (
                        flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )
                      )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={entityColumns.length}
                    className="h-24 text-center text-muted-foreground"
                  >
                    No entities match your search.
                  </TableCell>
                </TableRow>
              ) : (
                table.getRowModel().rows.map((row) => {
                  const dataIndex = rows.indexOf(row.original);
                  return (
                    <TableRow
                      key={row.id}
                      className={`cursor-pointer transition-colors ${
                        dataIndex === selectedRowIndex
                          ? "bg-primary/[0.06]"
                          : row.original.isPrimary
                            ? "bg-primary/[0.03] hover:bg-primary/[0.06]"
                            : "hover:bg-muted/50"
                      }`}
                      onClick={() => setSelectedRowIndex(dataIndex)}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id} className="px-4 py-2.5">
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </ScrollArea>
      </div>

      {/* Entity detail sheet */}
      <Sheet
        open={selectedEntity !== null}
        onOpenChange={(open) => {
          if (!open) setSelectedRowIndex(null);
        }}
      >
        <SheetContent
          side="right"
          className="w-full overflow-y-auto sm:max-w-2xl"
        >
          {selectedEntity && (
            <EntityDetailSheet
              entity={selectedEntity}
              relationships={selectedRelationships}
              onDocumentClick={onDocumentClick}
            />
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
